import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Button,
  Caption1,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import type { RejectionReason } from '@vigeo/shared';
import { KeyValueList, shellMedia, shellMetrics, tokens, useShellTier, type KeyValueItem } from '@vigeo/ui';
import { ReviewStepRenderer } from '@/steps';
import { approveSubmission, fetchReviewQueue, now as clockNow, rejectSubmission, type ReviewItem } from '@/lib/api';
import { RejectDialog } from './RejectDialog.js';
import { ageInDays, ageLabel, attemptLabel, itemChips, routingLabel, stepTypeLabel, submittedOn, type ChipTone } from './presenters.js';

const useStyles = makeStyles({
  /** Desktop tiers: the queue on the left, the selected submission on the right (Figma 311:5391). */
  split: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'start',
  },
  stacked: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  /** Reference card: flat white, radius 16, no border, one tinted shadow. */
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
  },
  list: {
    listStyleType: 'none',
    margin: 0,
    padding: tokens.spacingHorizontalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  item: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  /**
   * The queue row (Figma 311:5399): avatar 40, name and age on the first line, the pinned
   * heading on the second, signals on the third. The whole row is one control: it selects
   * (desktop) or discloses (stacked) the submission. Hover is the neutral hover, selection
   * is the brand wash, focus is the shared focus ring.
   */
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalS}`,
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    textAlign: 'left',
    cursor: 'pointer',
    minHeight: shellMetrics.touchTarget,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: `calc(-1 * ${tokens.strokeWidthThick})`,
    },
  },
  rowSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': { backgroundColor: tokens.colorBrandBackground2 },
  },
  rowBody: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0, flexGrow: 1 },
  rowTop: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalS },
  rowName: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowAge: { flexShrink: 0, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  rowHeading: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  /** Tags keep the small radius (checkpoint 7A). */
  chip: { borderRadius: tokens.borderRadiusSmall },
  /** The selected submission. Stacked tiers render it under its own row, inside the same card. */
  detail: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingHorizontalL, minWidth: 0 },
  inline: { borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, marginTop: tokens.spacingVerticalXXS },
  head: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalM },
  who: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0, flexGrow: 1 },
  /**
   * The person link: a flex row whose text truncates in an inner block, so the ellipsis
   * still renders; on phones the link itself is the 44px target (7A closeout decision 4).
   */
  person: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
    ':focus-visible': { textDecorationLine: 'underline' },
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  truncate: { display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  /** Section-title voice (16/600). */
  h2: { margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  h3: { margin: 0, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground2 },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  submission: { padding: tokens.spacingHorizontalM, borderRadius: tokens.borderRadiusLarge, backgroundColor: tokens.colorNeutralBackground3, minWidth: 0, overflowWrap: 'anywhere' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  action: { [shellMedia.phone]: { flexGrow: 1 } },
  viewOnly: { color: tokens.colorNeutralForeground3 },
  empty: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, padding: tokens.spacingHorizontalL },
  centered: { display: 'flex', justifyContent: 'center', paddingBlock: tokens.spacingVerticalXXXL },
  /**
   * Fluent's warning badge renders yellow shade30, 4.42:1 against an expanded row — under WCAG
   * AA's 4.5:1. The project's own warning colour clears it in both schemes (4.59:1 expanded,
   * 4.92:1 collapsed, 6.5–7.3:1 dark), and says "warning" in the same colour the dashboard does.
   */
  chipWarning: {
    color: tokens.colorComplianceWarning,
    borderTopColor: tokens.colorComplianceWarning,
    borderRightColor: tokens.colorComplianceWarning,
    borderBottomColor: tokens.colorComplianceWarning,
    borderLeftColor: tokens.colorComplianceWarning,
  },
});

const badgeColor = { neutral: 'informative', important: 'important', warning: 'warning' } as const satisfies Record<ChipTone, 'informative' | 'important' | 'warning'>;

function Chips({ item, now }: { readonly item: ReviewItem; readonly now: number }) {
  const s = useStyles();
  const chips = itemChips(item, now);
  if (chips.length === 0) return null;
  return (
    <span className={s.chips}>
      {chips.map((chip) => (
        <Badge key={chip.key} appearance="outline" size="medium" shape="rounded" color={badgeColor[chip.tone]} className={mergeClasses(s.chip, chip.tone === 'warning' && s.chipWarning)}>
          {chip.label}
        </Badge>
      ))}
    </span>
  );
}

interface DetailProps {
  readonly item: ReviewItem;
  readonly canDecide: boolean;
  readonly busy: 'approve' | 'reject' | null;
  readonly problem: string | null;
  readonly now: number;
  readonly id: string;
  readonly inline: boolean;
  readonly onApprove: () => void;
  readonly onSendBack: () => void;
}

/**
 * The selected submission: who, what, the facts the wire carries, exactly what they
 * submitted, and — for an admin — the decision. A viewer reads the same thing and is told
 * once that the decision is not theirs.
 */
function SubmissionDetail({ item, canDecide, busy, problem, now, id, inline, onApprove, onSendBack }: DetailProps) {
  const s = useStyles();
  const days = ageInDays(item.submittedAt, now);
  const date = submittedOn(item.submittedAt);
  const facts: KeyValueItem[] = [
    { label: 'Submitted', value: date === null ? 'Not recorded' : `${date} · ${ageLabel(days)}` },
    { label: 'Attempt', value: attemptLabel(item.attempt) },
    { label: 'Step type', value: stepTypeLabel(item.type) },
    { label: 'File routing', value: routingLabel(item.segregated) },
  ];
  return (
    <article id={id} className={mergeClasses(s.detail, inline && s.inline)} aria-labelledby={`${id}-heading`}>
      <div className={s.head}>
        <Avatar name={item.personName} size={40} color="neutral" aria-hidden />
        <div className={s.who}>
          <Link to={`/app/people/${item.personId}`} className={s.person} title={item.personName}>
            <span className={s.truncate}>{item.personName}</span>
          </Link>
          <Caption1>
            {stepTypeLabel(item.type)} · {ageLabel(days)}
          </Caption1>
        </div>
      </div>
      {/* The PINNED heading: what the person actually saw, never the definition's current wording. */}
      <h2 id={`${id}-heading`} className={s.h2}>
        {item.heading}
      </h2>
      {/* Side by side, the signals travel with the submission; disclosed under its own row they would only repeat it. */}
      {inline ? null : <Chips item={item} now={now} />}
      <KeyValueList aria-label="Submission facts" items={facts} />
      <section className={s.section} aria-label="What they submitted">
        <h3 className={s.h3}>What they submitted</h3>
        <div className={s.submission}>
          {/* Dispatched through the registry: this file names no step type and no requirement key. */}
          <ReviewStepRenderer type={item.type} config={item.config} submission={item.submission} />
        </div>
      </section>
      {problem === null ? null : (
        <MessageBar intent="warning">
          <MessageBarBody>{problem}</MessageBarBody>
        </MessageBar>
      )}
      {canDecide ? (
        <div className={s.actions}>
          <Button appearance="primary" className={s.action} disabled={busy !== null} onClick={onApprove}>
            {busy === 'approve' ? 'Approving…' : 'Approve'}
          </Button>
          <Button appearance="secondary" className={s.action} disabled={busy !== null} onClick={onSendBack}>
            Send back
          </Button>
        </div>
      ) : (
        <Caption1 className={s.viewOnly}>View only — an HR admin approves this or sends it back.</Caption1>
      )}
    </article>
  );
}

export interface ReviewQueueProps {
  /**
   * The submission to open on arrival, from `?instance=` — how step 5 of a person's record hands
   * HR the one row they clicked instead of the top of everybody's queue (#487, frame `277:6308`).
   *
   * A SEED, not a controlled value: HR selects freely afterwards. An id that is not in the queue
   * (decided already, or another page of it) falls through to the normal default, because landing
   * on an empty detail pane would read as a broken link rather than as "already dealt with".
   */
  readonly initialSelectedId?: string | null;
  /**
   * Whether this user may decide. Comes from the page's auth state (`isAdmin`), the same
   * source every other admin gate uses. Viewers still open and inspect submissions; the
   * decision controls and the send-back dialog are not rendered for them at all — defence
   * in depth in front of the API's own check, never a CSS hide.
   */
  readonly canDecide: boolean;
}

const EMPTY: readonly ReviewItem[] = [];

/**
 * The review queue (checkpoint 7C): the Inbox hierarchy from the reference, with VIGEO's
 * data. Desktop tiers show the queue beside the selected submission; below 1280 each row
 * discloses its submission in place. Every fact shown is a review API field or a presenter
 * projection of one.
 */
export function ReviewQueue({ canDecide, initialSelectedId = null }: ReviewQueueProps) {
  const s = useStyles();
  const client = useQueryClient();
  const tier = useShellTier();
  const stacked = tier !== 'wide' && tier !== 'desktop';
  const detailId = useId();
  const listRef = useRef<HTMLOListElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId);
  const [rejecting, setRejecting] = useState<ReviewItem | null>(null);
  // The control that opened the send-back dialog, so closing it puts the keyboard back where it was.
  const rejectTrigger = useRef<HTMLElement | null>(null);
  const closeReject = () => {
    setRejecting(null);
    const trigger = rejectTrigger.current;
    if (trigger !== null) window.setTimeout(() => trigger.focus(), 0);
  };
  const [problem, setProblem] = useState<string | null>(null);

  const queue = useQuery({ queryKey: ['review'], queryFn: () => fetchReviewQueue() });

  const decide = useMutation({
    mutationFn: async (input: { kind: 'approve'; item: ReviewItem } | { kind: 'reject'; item: ReviewItem; reason: RejectionReason; note: string; candidateComment: string }) => {
      if (input.kind === 'approve') return approveSubmission(input.item.instanceId, input.item.personId);
      return rejectSubmission(input.item.instanceId, input.item.personId, { reason: input.reason, note: input.note, candidateComment: input.candidateComment });
    },
    onSuccess: async () => {
      closeReject();
      setProblem(null);
      await client.invalidateQueries({ queryKey: ['review'] });
    },
    onError: (error: Error) => {
      // A 412 here means somebody else decided it first, which is information rather than
      // a fault — the list is refreshed so the reviewer sees the current truth. In fixture
      // mode every decision is refused the same way, and the refusal is shown, never hidden.
      setProblem(error.message);
      closeReject();
      void client.invalidateQueries({ queryKey: ['review'] });
    },
  });

  if (queue.isPending) {
    return (
      <div className={s.centered}>
        <Spinner label="Loading the queue" />
      </div>
    );
  }
  if (queue.isError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>The queue could not be loaded. {queue.error.message}</MessageBarBody>
        <MessageBarActions>
          <Button onClick={() => void queue.refetch()}>Try again</Button>
        </MessageBarActions>
      </MessageBar>
    );
  }

  const items = queue.data.items.length === 0 ? EMPTY : queue.data.items;
  const now = clockNow();
  // Desktop always shows a submission (the first, until one is chosen); stacked tiers start folded.
  const chosen = items.find((item) => item.instanceId === selectedId);
  const selected = chosen ?? (stacked ? undefined : items[0]);

  if (items.length === 0) {
    return (
      <section className={mergeClasses(s.card, s.empty)} aria-label="Nothing to review">
        <Text weight="semibold">Nothing is waiting for review.</Text>
        <Caption1>New submissions appear here the moment a candidate sends them.</Caption1>
      </section>
    );
  }

  const select = (id: string) => {
    setProblem(null);
    setSelectedId((current) => (stacked && current === id ? null : id));
  };

  const onRowKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const rows = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-queue-row]') ?? [])];
    const next = rows[rows.indexOf(event.currentTarget) + (event.key === 'ArrowDown' ? 1 : -1)];
    if (next === undefined) return;
    next.focus();
    // Side by side, moving through the queue is browsing it; folded rows only move focus.
    if (!stacked) select(next.dataset['queueRow'] ?? '');
  };

  const busy = decide.isPending && decide.variables !== undefined && decide.variables.item.instanceId === selected?.instanceId ? decide.variables.kind : null;

  const detail = (item: ReviewItem, inline: boolean) => (
    <SubmissionDetail
      item={item}
      canDecide={canDecide}
      busy={busy}
      problem={problem}
      now={now}
      id={detailId}
      inline={inline}
      onApprove={() => decide.mutate({ kind: 'approve', item })}
      onSendBack={() => { rejectTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; setRejecting(item); }}
    />
  );

  return (
    <div className={stacked ? s.stacked : s.split}>
      <div className={s.card}>
        <ol ref={listRef} className={s.list} aria-label="Waiting for review">
          {items.map((item) => {
            const isSelected = selected?.instanceId === item.instanceId;
            return (
              <li key={item.instanceId} className={s.item}>
                <button
                  type="button"
                  data-queue-row={item.instanceId}
                  className={mergeClasses(s.row, isSelected && s.rowSelected)}
                  aria-current={!stacked && isSelected ? 'true' : undefined}
                  aria-expanded={stacked ? isSelected : undefined}
                  aria-controls={isSelected ? detailId : undefined}
                  onClick={() => select(item.instanceId)}
                  onKeyDown={onRowKey}
                >
                  <Avatar name={item.personName} size={40} color="neutral" aria-hidden />
                  <span className={s.rowBody}>
                    <span className={s.rowTop}>
                      <span className={s.rowName} title={item.personName}>
                        {item.personName}
                      </span>
                      <span className={s.rowAge}>{ageLabel(ageInDays(item.submittedAt, now))}</span>
                    </span>
                    <span className={s.rowHeading} title={item.heading}>
                      {item.heading}
                    </span>
                    <Chips item={item} now={now} />
                  </span>
                </button>
                {stacked && isSelected ? detail(item, true) : null}
              </li>
            );
          })}
        </ol>
      </div>

      {!stacked && selected !== undefined ? <div className={s.card}>{detail(selected, false)}</div> : null}

      {canDecide ? (
        <RejectDialog
          open={rejecting !== null}
          itemKey={rejecting?.instanceId ?? ''}
          heading={rejecting?.heading ?? ''}
          busy={decide.isPending}
          onCancel={closeReject}
          onConfirm={({ reason, note, candidateComment }) => {
            if (rejecting !== null) decide.mutate({ kind: 'reject', item: rejecting, reason, note, candidateComment });
          }}
        />
      ) : null}
    </div>
  );
}
